/**
 * Framer Motion Animation Components
 * 
 * Replaces all CSS animations with proper Framer Motion equivalents
 * for reliable rendering across browsers.
 */

import React from 'react';
import { motion } from 'framer-motion';

/**
 * Pulse animation - Gentle opacity/scale breathing effect
 * Replaces: animate-pulse, animate-subtle-pulse
 */
export const Pulse = ({
    children,
    className = '',
    duration = 2,
    subtle = false,
    ...props
}) => (
    <motion.div
        className={className}
        animate={subtle ? {
            opacity: [0.5, 0.8, 0.5],
            scale: [1, 1.05, 1]
        } : {
            opacity: [1, 0.5, 1]
        }}
        transition={{
            duration,
            repeat: Infinity,
            ease: 'easeInOut'
        }}
        {...props}
    >
        {children}
    </motion.div>
);

/**
 * Float animation - Gentle vertical bobbing motion
 * Replaces: animate-float, animate-slow-float
 */
export const Float = ({
    children,
    className = '',
    duration = 4,
    distance = 4,
    slow = false,
    ...props
}) => (
    <motion.div
        className={className}
        animate={{
            y: [0, slow ? -8 : -distance, 0]
        }}
        transition={{
            duration: slow ? 12 : duration,
            repeat: Infinity,
            ease: 'easeInOut'
        }}
        style={slow ? { willChange: 'transform' } : undefined}
        {...props}
    >
        {children}
    </motion.div>
);

/**
 * Sparkle animation - Twinkling opacity + glow effect
 * Replaces: animate-sparkle
 */
export const Sparkle = ({
    children,
    className = '',
    duration = 2,
    ...props
}) => (
    <motion.div
        className={className}
        animate={{
            opacity: [0.4, 1, 0.4],
            filter: [
                'drop-shadow(0 0 2px rgba(220, 38, 38, 0.3))',
                'drop-shadow(0 0 8px rgba(220, 38, 38, 0.6))',
                'drop-shadow(0 0 2px rgba(220, 38, 38, 0.3))'
            ]
        }}
        transition={{
            duration,
            repeat: Infinity,
            ease: 'easeInOut'
        }}
        {...props}
    >
        {children}
    </motion.div>
);

/**
 * SlideUp animation - Entry animation from below
 * Replaces: animate-slide-up
 */
export const SlideUp = ({
    children,
    className = '',
    duration = 0.3,
    distance = 10,
    ...props
}) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y: distance }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
            duration,
            ease: 'easeOut'
        }}
        {...props}
    >
        {children}
    </motion.div>
);

/**
 * FadeIn animation - Simple fade entry
 * Replaces: animate-fade-in
 */
export const FadeIn = ({
    children,
    className = '',
    duration = 0.3,
    ...props
}) => (
    <motion.div
        className={className}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
            duration,
            ease: 'easeOut'
        }}
        {...props}
    >
        {children}
    </motion.div>
);

/**
 * FadeInUp animation - Entry with upward motion
 * Replaces: animate-fade-in-up, animate-fade-in-up-delay-*
 */
export const FadeInUp = ({
    children,
    className = '',
    duration = 0.6,
    delay = 0,
    distance = 30,
    ...props
}) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y: distance }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
            duration,
            delay,
            ease: 'easeOut'
        }}
        {...props}
    >
        {children}
    </motion.div>
);

/**
 * BorderGlow animation - Pulsing border color
 * Replaces: animate-border-glow
 */
export const BorderGlow = ({
    children,
    className = '',
    duration = 3,
    ...props
}) => (
    <motion.div
        className={className}
        animate={{
            borderColor: [
                'rgba(220, 38, 38, 0.2)',
                'rgba(220, 38, 38, 0.5)',
                'rgba(220, 38, 38, 0.2)'
            ]
        }}
        transition={{
            duration,
            repeat: Infinity,
            ease: 'easeInOut'
        }}
        {...props}
    >
        {children}
    </motion.div>
);

/**
 * Shimmer animation - Loading skeleton shimmer
 * Replaces: .shimmer class
 */
export const Shimmer = ({
    className = '',
    ...props
}) => (
    <motion.div
        className={className}
        style={{
            background: 'linear-gradient(90deg, rgba(185, 28, 28, 0) 0%, rgba(185, 28, 28, 0.06) 50%, rgba(185, 28, 28, 0) 100%)',
            backgroundSize: '200% 100%',
        }}
        animate={{
            backgroundPosition: ['-200% 0', '200% 0']
        }}
        transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'linear'
        }}
        {...props}
    />
);

/**
 * Spin animation - Continuous rotation
 * Replaces: animate-spin-slow (use Spinner for regular spin)
 */
export const SpinSlow = ({
    children,
    className = '',
    duration = 2,
    ...props
}) => (
    <motion.div
        className={className}
        animate={{
            rotate: 360
        }}
        transition={{
            duration,
            repeat: Infinity,
            ease: 'linear'
        }}
        {...props}
    >
        {children}
    </motion.div>
);

// Re-export Spinner for consistency
export { default as Spinner } from './Spinner';
